import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, BadgeCheck, TrendingUp, Megaphone } from "lucide-react";
import { track } from "@/lib/analytics";
import { BRAND } from "@/data/content";

const IMG_PORTRAIT =
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=1000&q=80";
const IMG_WORKSPACE =
  "https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=800";

const AD_METRICS = [
  ["ROAS", "4.2x"],
  ["CTR", "3.8%"],
  ["Sales", "$5.1k"],
];

const GROWTH_BARS = [34, 42, 50, 58, 70, 84, 100];

function AdCard() {
  return (
    <div className="bg-white rounded-2xl shadow-[0_18px_50px_rgba(10,10,15,0.14)] border border-neutral-100 p-4 w-[210px]" data-testid="hero-ad-card">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold text-neutral-700">
          <Megaphone size={11} className="text-gold" strokeWidth={2.2} />
          Summer Glow Campaign
        </span>
        <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-600">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {AD_METRICS.map(([k, v]) => (
          <div key={k} className="rounded-lg bg-[#FAF8F2] px-2 py-2 text-center">
            <p className="text-[8.5px] font-semibold uppercase tracking-wider text-neutral-400">{k}</p>
            <p className="font-display text-[13px] font-bold text-aurora-static">{v}</p>
          </div>
        ))}
      </div>
      <p className="mt-2.5 text-[9.5px] text-neutral-400">$1.2k spend · winning ad found in 6 days</p>
    </div>
  );
}

function GrowthCard() {
  return (
    <div className="bg-white rounded-2xl shadow-[0_18px_50px_rgba(10,10,15,0.14)] border border-neutral-100 p-4 w-[196px]" data-testid="hero-growth-card">
      <p className="flex items-center gap-1.5 text-[10px] font-semibold text-neutral-700">
        <TrendingUp size={11} className="text-gold" strokeWidth={2.4} />
        Social growth
      </p>
      <p className="font-display text-lg font-bold text-neutral-900 mt-1 leading-none">
        +12,400 <span className="text-[10px] font-semibold text-neutral-400">followers</span>
      </p>
      <div className="mt-3 flex items-end gap-1 h-9">
        {GROWTH_BARS.map((h, i) => (
          <motion.span
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ duration: 0.7, delay: 0.9 + i * 0.08 }}
            className="flex-1 rounded-t"
            style={{ background: "linear-gradient(180deg, #D4AF37, #B8860B)" }}
          />
        ))}
      </div>
      <p className="mt-2 text-[9.5px] text-neutral-400">90 days · organic + paid</p>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const orbY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative hero-glow grain overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ y: orbY }}
        className="absolute inset-0 pointer-events-none z-0"
      >
        <div
          className="aurora-orb aurora-orb-magenta orb-anim-a"
          style={{ width: 420, height: 420, top: -120, left: -180 }}
        />
        <div
          className="aurora-orb aurora-orb-azure orb-anim-b"
          style={{ width: 380, height: 380, top: 160, right: -200 }}
        />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 pt-32 sm:pt-40 pb-16 sm:pb-24 w-full"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="overline"
              data-testid="hero-eyebrow"
            >
              A premium success studio for founders & modern brands
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-[2.9rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] font-bold tracking-tighter mt-6 sm:mt-7 text-neutral-900"
              data-testid="hero-headline"
            >
              Where ambitious
              <br />
              brands <span className="text-aurora">are built.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="text-base sm:text-lg text-neutral-500 leading-relaxed mt-7 max-w-lg"
              data-testid="hero-subhead"
            >
              <span className="text-neutral-900 font-semibold">{BRAND.name}</span> builds
              your brand, writes your marketing and gives you a storefront that sells.
              <em className="text-gold-grad not-italic font-semibold"> One studio, one subscription</em>
              {" "}- from $49/mo.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45 }}
              className="flex flex-col sm:flex-row gap-4 mt-9"
            >
              <Link
                to="/pricing"
                className="btn-aurora"
                data-testid="hero-primary-cta"
                onClick={() => track("CTA Clicked", { location: "hero", label: "Get started" })}
              >
                Get started <ArrowUpRight size={16} />
              </Link>
              <a
                href="#how-it-works"
                className="btn-ghost"
                data-testid="hero-secondary-cta"
                onClick={() => track("CTA Clicked", { location: "hero", label: "Watch the demo" })}
              >
                Watch the demo <ArrowDown size={16} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-9 flex flex-wrap items-center gap-x-6 sm:gap-x-8 gap-y-3 text-[11px] sm:text-xs uppercase tracking-[0.22em] text-neutral-400"
              data-testid="hero-trust-strip"
            >
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gold" /> 4.9 / 5 rating
              </span>
              <span className="hidden sm:inline-block w-px h-3 bg-neutral-300" aria-hidden />
              <span>3,200+ founders</span>
              <span className="hidden sm:inline-block w-px h-3 bg-neutral-300" aria-hidden />
              <span>SOC 2 in progress</span>
              <span className="hidden sm:inline-block w-px h-3 bg-neutral-300" aria-hidden />
              <span>Cancel anytime</span>
            </motion.div>

            <p className="mt-6 text-xs text-neutral-400">
              Starter from <span className="text-aurora-static font-semibold">$49/mo</span> · Growth <span className="text-aurora-static font-semibold">$199/mo</span> · Annual saves 20%.
            </p>
          </div>

          {/* Visual collage: real ads, social growth, personal branding */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25 }}
            className="lg:col-span-6 relative h-[480px] sm:h-[560px]"
            data-testid="hero-visual-collage"
          >
            <div className="absolute right-0 top-0 w-[64%] h-[76%] rounded-3xl overflow-hidden rotate-1 shadow-[0_30px_80px_rgba(10,10,15,0.18)]">
              <img
                src={IMG_PORTRAIT}
                alt="A founder building her personal brand with GoldKey Studio."
                className="w-full h-full object-cover"
                loading="eager"
              />
              <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[10px] font-bold text-neutral-800">
                <BadgeCheck size={12} className="text-gold" strokeWidth={2.2} />
                Personal brand · Verified
              </span>
            </div>

            <div className="absolute left-0 bottom-[2%] w-[46%] h-[38%] rounded-2xl overflow-hidden border-4 border-white shadow-[0_24px_60px_rgba(10,10,15,0.16)]">
              <img
                src={IMG_WORKSPACE}
                alt="A business owner celebrating growth from her social media marketing."
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="absolute left-[2%] top-[4%] z-10 mz-float">
              <AdCard />
            </div>

            <div className="absolute right-[2%] bottom-[6%] z-10 mz-float-delay">
              <GrowthCard />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
